import { getUserDetails } from '../../../utils/helper';
import type { ReportParts } from '../types';

/**
 * Extract header, body, and footer from the full editor HTML.
 * The editor content contains `.page-header-content` and `.page-footer-content`
 * wrappers injected by TinyMCE setup — this function strips them out and returns
 * each part separately so they can be stored in individual DB columns.
 */
export const extractReportParts = (editorContent: string): ReportParts => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(editorContent, 'text/html');

  const headerEl = doc.querySelector('.page-header-content');
  const footerEl = doc.querySelector('.page-footer-content');

  const header = headerEl ? headerEl.outerHTML : '';
  const footer = footerEl ? footerEl.outerHTML : '';

  if (headerEl) headerEl.remove();
  if (footerEl) footerEl.remove();

  const body = doc.body.innerHTML;
  return { header, body, footer };
};

/**
 * Update the header's datetime and radiologist name in-place.
 * Called right before saving when status === SIGNEDOFF.
 */
export const updateHeaderOnSignoff = (editorContent: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(editorContent, 'text/html');

  const dateTimeCell = doc.getElementById('report-datetime-value');
  if (dateTimeCell) {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const formattedTime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    dateTimeCell.innerHTML = `<strong>${formattedTime}</strong>`;
  }

  const radiologistCell = doc.getElementById('radiologist-value');
  if (radiologistCell) {
    const currentUser = getUserDetails();
    const userName = currentUser?.user_fullname || '-';
    radiologistCell.innerHTML = `<strong>${userName}</strong>`;
  }

  return doc.body.innerHTML;
};

/**
 * Strip auto-generated elements (header, footer, auto page-breaks)
 * to get just the medical content — used for printing.
 */
export const extractMedicalContent = (fullContent: string): string => {
  if (!fullContent || fullContent === '<div></div>' || fullContent === '') {
    return '<div></div>';
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = fullContent;

  tempDiv
    .querySelectorAll('.page-header-content, .page-footer-content, .mce-pagebreak.auto-break')
    .forEach(el => el.remove());

  return tempDiv.innerHTML.trim() || '<div></div>';
};

/**
 * Check whether HTML content is "empty" (no real text, just empty tags / whitespace).
 */
export const isContentEmpty = (html: string | null | undefined): boolean => {
  if (!html) return true;
  const trimmed = html.trim();
  if (trimmed === '' || trimmed === '<div></div>' || trimmed === '<div><br></div>' || trimmed === '<p></p>' || trimmed === '<p><br></p>') {
    return true;
  }
  const textOnly = trimmed.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '').trim();
  return textOnly === '';
};
